import type { TFunction } from "i18next";
import { differenceInCalendarDays } from "date-fns";
import { formatPerson } from "@/utils/helper";
import type {
  ChatMuteDuration,
  ChatWorkStatus,
  IChatConversation,
  IChatConversationDetail,
  IChatLastMessage,
  IChatMessageMetadata,
  IChatPeer,
  IChatPresence,
} from "@/types/(my-space)/chat.interface";
import { chatShortcutActionFor } from "./chat-shortcuts";
import { stripMarkdown } from "./markdown";
import { channelName, noticePreview } from "./channel-notices";

/**
 * Display text for chat. Everything is computed at render time so it follows
 * a language switch; API values (types, statuses, events) stay raw elsewhere.
 * Plain helpers: `t` and the locale's date formatter are passed in.
 */

type FormatDate = (date: Date | number, pattern?: string) => string;

export const personName = (
  t: TFunction,
  p: { firstName?: string | null; lastName?: string | null } | null | undefined,
) => formatPerson(p, t("chat:labels.unknown_user"));

export const chatWorkStatusAudience = (t: TFunction, ws: ChatWorkStatus) =>
  ({
    clocked_in: t("chat:labels.audience_clocked_in"),
    on_break: t("chat:labels.audience_on_break"),
    clocked_out: t("chat:labels.audience_clocked_out"),
  })[ws];

export const chatWorkStatusOption = (t: TFunction, ws: ChatWorkStatus) =>
  ({
    clocked_in: t("chat:labels.work_status_clocked_in"),
    on_break: t("chat:labels.work_status_on_break"),
    clocked_out: t("chat:labels.work_status_clocked_out"),
  })[ws];

export const conversationTitle = (
  t: TFunction,
  c: Pick<IChatConversation, "type" | "name" | "metadata"> & {
    /** A self chat's peer is me: titled "<my name> (You)". */
    isSelf?: boolean;
    peer: { firstName: string; lastName: string } | null;
  },
) => {
  if (c.isSelf) return t("chat:labels.self_title", { name: personName(t, c.peer) });
  if (c.type === "direct") return personName(t, c.peer);
  // An app channel reads in my language, whatever name the server stored.
  if (c.type === "channel") {
    return channelName(t, c.metadata && "channel" in c.metadata ? c.metadata.channel : undefined);
  }
  const name = c.name?.trim();
  if (name) return name;
  if (c.metadata && "source" in c.metadata && c.metadata.source === "work_status") {
    return chatWorkStatusAudience(t, c.metadata.workStatus);
  }
  return c.type === "broadcast"
    ? t("chat:labels.broadcast_untitled")
    : t("chat:labels.group_untitled");
};

/**
 * Whether the "Message yourself" row belongs in a contact search: always when
 * the search is empty, else when it matches my name or the "You" label —
 * case- and accent-insensitive, any word order.
 */
export const selfRowMatchesSearch = (
  t: TFunction,
  me: { firstName?: string | null; lastName?: string | null },
  search: string,
) => {
  const fold = (s: string) =>
    s.normalize("NFD").replace(/[\u0300-\u036f\u064b-\u065f]/g, "").toLocaleLowerCase();
  const words = fold(search).split(/\s+/).filter(Boolean);
  if (!words.length) return true;
  const haystack = fold(
    [personName(t, me), t("chat:info.you_label"), t("chat:new.message_yourself")].join(" "),
  );
  return words.every((w) => haystack.includes(w));
};

/** The composer banner for a direct chat, or null while the peer is working. */
export const peerAvailabilityBanner = (t: TFunction, peer: IChatPeer) => {
  if (peer.workStatus === "clocked_in") return null;
  return peer.workStatus === "on_break"
    ? t("chat:labels.peer_on_break", { name: peer.firstName })
    : t("chat:labels.peer_not_clocked_in", { name: peer.firstName });
};

/** Short description of a message's content (list preview, reply quote). */
export const messageContentPreview = (
  t: TFunction,
  m: {
    type: string;
    body?: string | null;
    metadata?: IChatMessageMetadata | null;
    attachmentCount?: number;
    isDeleted?: boolean;
  },
  /** Names me as "You" in a system line. */
  myUserId?: number,
) => {
  if (m.isDeleted) return t("chat:labels.message_deleted");
  // Formatting symbols (**, `, > …) don't belong in a one-line preview.
  const caption = stripMarkdown(m.body?.trim() ?? "");
  const count = m.attachmentCount ?? 0;
  const withCaption = (label: string) =>
    caption ? t("chat:preview.with_caption", { label, caption }) : label;
  const meta = m.metadata ?? {};
  switch (m.type) {
    case "text":
      return caption;
    case "image":
      return withCaption(
        count > 1 ? t("chat:preview.photos", { count }) : t("chat:preview.photo"),
      );
    case "video":
      return withCaption(t("chat:preview.video"));
    case "voice":
      return t("chat:preview.voice");
    case "file":
      return withCaption(
        count > 1 ? t("chat:preview.files", { count }) : t("chat:preview.file"),
      );
    case "gif":
      return t("chat:preview.gif");
    case "location":
      return t("chat:preview.location");
    case "contact":
      return meta.contact?.name
        ? t("chat:preview.with_caption", {
            label: t("chat:preview.contact"),
            caption: meta.contact.name,
          })
        : t("chat:preview.contact");
    case "poll":
      return withCaption(t("chat:preview.poll"));
    case "link":
      return caption || meta.link?.url || t("chat:preview.link");
    case "shortcut": {
      const sc = meta.shortcut;
      if (sc?.entity?.title) return sc.entity.title;
      const action = chatShortcutActionFor(sc?.action);
      if (action) return action.label(t);
      return sc?.label || t("chat:preview.shortcut");
    }
    case "notice":
      return noticePreview(t, meta.notice);
    case "system":
      return meta.system
        ? systemMessageText(t, meta.system, { names: new Map(), myUserId })
        : "";
    default:
      return caption;
  }
};

export const lastMessagePreview = (
  t: TFunction,
  c: IChatConversation,
  myUserId: number | undefined,
) => {
  const m: IChatLastMessage | null = c.lastMessage;
  if (!m) return t("chat:labels.new_chat");
  const content = messageContentPreview(t, m, myUserId);
  if (m.type === "system" || m.isDeleted) return content;
  // In my own notes every message is mine: "You:" would only add noise.
  if (c.isSelf) return content;
  if (m.senderId != null && m.senderId === myUserId) {
    return t("chat:preview.you", { message: content });
  }
  if (c.type !== "direct" && m.senderName) {
    return t("chat:preview.sender", {
      name: m.senderName.split(" ")[0],
      message: content,
    });
  }
  return content;
};

/** System events are stored as keys + ids; the sentence is built here. */
export const systemMessageText = (
  t: TFunction,
  system: NonNullable<IChatMessageMetadata["system"]>,
  { names, myUserId }: { names: Map<number, string>; myUserId?: number },
) => {
  const nameOf = (id: number | undefined) => {
    if (id == null) return t("chat:system.someone");
    if (id === myUserId) return t("chat:system.you");
    // A current member's name, else the one stored with the event (someone
    // who has since left, or the list, which knows no members at all).
    return names.get(id) || system.names?.[String(id)] || t("chat:system.someone");
  };
  const actor = nameOf(system.actorId);
  const members = (system.targetUserIds ?? [])
    .map(nameOf)
    .join(t("chat:system.list_separator"));
  const name = system.name ?? "";
  switch (system.event) {
    case "group_created":
      return t("chat:system.group_created", { actor, name });
    case "broadcast_created":
      return t("chat:system.broadcast_created", { actor });
    case "members_added":
      return t("chat:system.members_added", { actor, members });
    case "member_removed":
      return t("chat:system.member_removed", { actor, members });
    case "member_left":
      return t("chat:system.member_left", { actor });
    case "renamed":
      return t("chat:system.renamed", { actor, name });
    case "image_changed":
      return t("chat:system.image_changed", { actor });
    case "message_pinned":
      return t("chat:system.message_pinned", { actor });
    default:
      return t("chat:system.updated");
  }
};

/** 15:17 today, "Yesterday", weekday this week, else a short date. */
export const conversationTime = (
  t: TFunction,
  formatDate: FormatDate,
  iso: string,
  now = new Date(),
) => {
  const d = new Date(iso);
  const diff = differenceInCalendarDays(now, d);
  if (diff <= 0) return formatDate(d, "p");
  if (diff === 1) return t("chat:time.yesterday");
  if (diff < 7) return formatDate(d, "EEE");
  return formatDate(d, "P");
};

/** Day separator inside a conversation. */
export const dayLabel = (
  t: TFunction,
  formatDate: FormatDate,
  iso: string,
  now = new Date(),
) => {
  const d = new Date(iso);
  const diff = differenceInCalendarDays(now, d);
  if (diff <= 0) return t("chat:time.today");
  if (diff === 1) return t("chat:time.yesterday");
  if (d.getFullYear() === now.getFullYear()) return formatDate(d, "MMMM d");
  return formatDate(d, "PPP");
};

/**
 * When a scheduled message goes out: "Today at 14:00", "Tomorrow at 09:00",
 * "Monday at 09:00" within the week, else the date.
 */
export const scheduledTimeLabel = (
  t: TFunction,
  formatDate: FormatDate,
  at: Date | string,
  now = new Date(),
) => {
  const d = new Date(at);
  const diff = differenceInCalendarDays(d, now);
  const time = formatDate(d, "p");
  const day =
    diff === 0
      ? t("chat:time.today")
      : diff === 1
        ? t("chat:time.tomorrow")
        : diff > 1 && diff < 7
          ? formatDate(d, "EEEE")
          : d.getFullYear() === now.getFullYear()
            ? formatDate(d, "MMMM d")
            : formatDate(d, "PPP");
  return t("chat:scheduled.day_at_time", { day, time });
};

/** "Online" / "Last seen today at 14:05" / "Offline"; null before the server answered. */
export const presenceLabel = (
  t: TFunction,
  formatDate: FormatDate,
  p: IChatPresence | undefined,
  now = new Date(),
) => {
  if (!p) return null;
  if (p.online) return t("chat:status.online");
  if (!p.lastSeenAt) return t("chat:status.offline");
  const seen = new Date(p.lastSeenAt);
  const diff = differenceInCalendarDays(now, seen);
  const time = formatDate(seen, "p");
  if (diff <= 0) return t("chat:status.last_seen_today", { time });
  if (diff === 1) return t("chat:status.last_seen_yesterday", { time });
  return t("chat:status.last_seen_on", { date: formatDate(seen, "PP") });
};

export const muteDurationLabel = (t: TFunction, d: ChatMuteDuration) =>
  ({
    "1h": t("chat:mute.1h"),
    "8h": t("chat:mute.8h"),
    "1w": t("chat:mute.1w"),
    always: t("chat:mute.always"),
    off: t("chat:mute.on"),
  })[d];

/** "12 members" / "11 recipients" — a broadcast's owner isn't a recipient. */
export const memberCountLabel = (
  t: TFunction,
  d: Pick<IChatConversationDetail, "type" | "memberCount">,
) =>
  d.type === "broadcast"
    ? t("chat:status.recipients", { count: Math.max(d.memberCount - 1, 0) })
    : t("chat:status.members", { count: d.memberCount });

/** Seconds → "1:05". */
export const durationLabel = (ms: number) => {
  const total = Math.max(0, Math.round(ms / 1000));
  const m = Math.floor(total / 60);
  const s = String(total % 60).padStart(2, "0");
  return `${m}:${s}`;
};
